import { combineReducers } from 'redux';

import appReducer, * as fromApp from 'app/modules/app';
import configuration, * as fromConfiguration from 'app/modules/configuration';
import demoKbReducer, * as fromKnowledgeBase from 'app/modules/knowledge_base/demo'; // DEMO ONLY
import messages, * as fromMessages from 'app/modules/messages';
import participants, * as fromParticipants from 'app/modules/participants';
import user, * as fromUser from 'app/modules/user';
import ui, * as fromUi from 'app/modules/ui';
import session, * as fromSession from 'app/modules/session';
import selfService, * as fromSelfService from 'app/modules/self_service';

// Root reducer
export default combineReducers({
  answers: demoKbReducer,
  app: appReducer,
  configuration,
  session,
  messages,
  participants,
  selfService,
  ui,
  user,
});

// Initial state

export const initialState = {
  answers: fromKnowledgeBase.initialState,
  configuration: fromConfiguration.initialState,
  messages: fromMessages.initialState,
  participants: fromParticipants.initialState,
  selfService: fromSelfService.initialState,
  ui: fromUi.initialState,
  user: fromUser.initialState,
};

// Configuration selectors
export const isSmoochEnabled = state => {
  return fromConfiguration.isSmoochEnabled(state.configuration);
};

export const getAlignment = state => {
  return fromConfiguration.getAlignment(state.configuration);
};

export const getBorder = state => {
  return fromConfiguration.getBorder(state.configuration);
};

export const getBorderRadius = state => {
  return fromConfiguration.getBorderRadius(state.configuration);
};

export const getWelcomeMessage = state => {
  let sessionWelcomeMessage = fromSession.getSessionWelcomeMessage(state.session);
  let configWelcomeMessage = fromConfiguration.getOngoingChat(state.configuration).welcomeMessage;
  return sessionWelcomeMessage || configWelcomeMessage;
};

export const hasActiveSession = state => {
  return fromSession.hasActiveSession(state.session);
};

export const getColors = state => {
  return fromConfiguration.getColors(state.configuration);
};

export const getInitialChatButton = state => {
  return fromConfiguration.getInitialChatButton(state.configuration);
};

export const getIsDraggable = state => {
  return fromConfiguration.getIsDraggable(state.configuration);
};

export const isDemoSelfServiceEnabled = state => {
  return fromConfiguration.isDemoSelfServiceEnabled(state.configuration);
};

export const getOnboarding = state => {
  return fromConfiguration.getOnboarding(state.configuration);
};

export const getOngoingChat = state => {
  return fromConfiguration.getOngoingChat(state.configuration);
};

export const getDropshadowEnabled = state => {
  return fromConfiguration.getDropshadowEnabled(state.configuration);
};

export const getFont = state => {
  return fromConfiguration.getFont(state.configuration);
};

export const getWidgetType = state => {
  return fromConfiguration.getWidgetType(state.configuration);
};

export const getZIndex = state => {
  return fromConfiguration.getZIndex(state.configuration);
};

// Knowledge Base selectors
export const getAnswers = state => {
  return fromKnowledgeBase.getAnswers(state.answers);
};

export const getSuggestedAnswers = state => {
  return fromKnowledgeBase.getSuggestedAnswers(state.answers);
};

export const getKBSearchResults = state => {
  return fromKnowledgeBase.getKBSearchResults(state.answers);
};

export const getAnswerById = (state, id) => {
  return fromKnowledgeBase.getAnswerById(state.answers, id);
};

export const getSelectedAnswer = state => {
  return fromKnowledgeBase.getSelectedAnswer(state.answers);
};

// Self Service selectors
export const getSelfServiceAnswers = state => {
  return fromSelfService.getAnswers(state.selfService);
};

export const getQuickActions = state => {
  return fromSelfService.getQuickActions(state.selfService);
};

export const getSelfServiceSearchResults = state => {
  return fromSelfService.getKBSearchResults(state.selfService);
};

export const getSelfServiceAnswerById = (state, id) => {
  return fromSelfService.getAnswerById(state.selfService, id);
};

export const getSelectedSelfServiceAnswer = state => {
  return fromSelfService.getSelectedAnswer(state.selfService);
};

// Message selectors

export const getMessages = state => {
  const userId = getUserId(state);
  const messages = fromMessages.getMessages(state.messages);
  if (isSmoochEnabled(state)) {
    return messages;
  }

  return messages.map(message => ({
    ...message,
    isUser: message.participantId === userId,
  }));
};

export const getUnreadAgentMessages = state => {
  const userId = getUserId(state);
  let dateMinimized = fromUi.getminimizedAt(state.ui);
  const messagesAfterDate = fromMessages.getMessagesAfterDate(state.messages, dateMinimized);

  if (isSmoochEnabled(state)) {
    return messagesAfterDate.filter(message => !message.isUser);
  }
  return messagesAfterDate.filter(message => message.participantId !== userId);
};

export const getMessageById = (state, id) => {
  return fromMessages.getMessageById(state.messages, id);
};

// Participant selectors

export const getParticipants = state => {
  return fromParticipants.getParticipants(state.participants);
};

export const getParticipantById = (state, id) => {
  return fromParticipants.getParticipantById(state.participants, id);
};

// User selectors

export const getUserId = state => {
  return fromUser.getUserId(state.user);
};

export const getUserGladlyCustomerId = state => {
  return fromUser.getGladlyCustomerId(state.user);
};

export const getUserName = state => {
  return fromUser.getName(state.user);
};

export const getUserEmail = state => {
  return fromUser.getEmail(state.user);
};

// UI selectors

export const getIsVisible = state => {
  return fromUi.getIsVisible(state.ui);
};

export const getIsMinimized = state => {
  return fromUi.getIsMinimized(state.ui);
};

export const getStage = state => {
  return fromUi.getStage(state.ui);
};

export const getCompositionDraft = state => {
  return fromUi.getCompositionDraft(state.ui);
};

// APP selectors

export const getAuthError = state => {
  return fromApp.getAuthError(state.app);
};

export const getProactiveTriggered = state => {
  return fromApp.getProactiveTriggered(state.app);
};

export const isAppInitialized = state => {
  return fromApp.isAppInitialized(state.app);
};

export const isClientReady = state => {
  return fromApp.isClientReady(state.app);
};
